import { useCallback } from 'react';

/**
 * Hook for sending chat messages
 * @param {Object} params - Parameters
 * @param {Object} params.socket - Socket instance
 * @param {string} params.connectionStatus - Connection status
 * @param {Object} params.userInfo - Current user info
 * @param {string} params.userType - User type (profesionales/empresas)
 * @param {string} params.employeeId - Employee ID
 * @param {string} params.restaurantUserId - Restaurant user ID
 * @param {string} params.conversationId - Conversation ID
 * @param {Function} params.setMessages - Function to update messages
 * @returns {Function} handleSendMessage function
 */
const useSendMessage = ({
  socket,
  connectionStatus,
  userInfo,
  userType,
  employeeId,
  restaurantUserId,
  conversationId,
  setMessages
}) => {
  const handleSendMessage = useCallback((message) => {
    if (!message.trim() || !socket || connectionStatus !== 'connected') {
      return;
    }

    if (!userInfo) {
      return;
    }

    const isProfessional = userType === 'profesionales';
    const isBusiness = userType === 'empresas';
  
    // Get senderUserId from userInfo
    let senderUserId = isProfessional ? userInfo?.employeeId : userInfo?.restaurantUserId;
    
    // For admin/staff users (company users without restaurantUserId), use userId
    if (!senderUserId && isBusiness && userInfo?.userId) {
      senderUserId = userInfo.userId;
    }
    const receiverId = isProfessional ? restaurantUserId : employeeId;
    const senderType = isProfessional ? 'employee' : 'restaurant';
    const receiverType = isProfessional ? 'restaurant' : 'employee';
  
    if (!senderUserId) {
      return;
    }
  
    if (!receiverId) {
      return;
    }
  
    const newMessage = {
      text: message.trim(),
      senderUserId,
      receiverUserId: receiverId,
      conversationId,
      senderType,
      receiverType,
      timestamp: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      // Add the correct sender fields to match database structure
      senderEmployeeId: isProfessional ? senderUserId : null,
      senderRestaurantUserId: isBusiness ? senderUserId : null,
      receiverEmployeeId: isProfessional ? null : receiverId,
      receiverRestaurantUserId: isProfessional ? receiverId : null,
    };
  
    try {
      socket.emit('sendMessage', newMessage);
      setMessages((prev) => [...prev, newMessage]);
      return true; // Success
    } catch (error) {
      // Failed to send message
      return false;
    }
  }, [socket, connectionStatus, userType, employeeId, restaurantUserId, userInfo, conversationId, setMessages]);

  return handleSendMessage;
};

export default useSendMessage;

